import React, { useState, useEffect } from 'react';
import { loansApi } from '../api';
import { formatCurrency } from '../utils/currency';

interface PaymentData {
  month: string;
  amount: number;
  count: number;
}

export const PaymentHistoryChart: React.FC = () => {
  const [paymentData, setPaymentData] = useState<PaymentData[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchPaymentHistory();
  }, []);

  const fetchPaymentHistory = async () => {
    try {
      // Fetch all loans to get payment history
      const loansResponse = await loansApi.getAll(1, 100);
      if (loansResponse.success && loansResponse.data) {
        const loans = loansResponse.data;

        // Fetch payments for each loan
        const allPayments: any[] = [];
        for (const loan of loans) {
          const paymentsResponse = await loansApi.getPayments(loan.loan_id);
          if (paymentsResponse.success && paymentsResponse.data) {
            allPayments.push(...paymentsResponse.data.payments.filter((p: any) => p.status === 'successful'));
          }
        }

        // Group by month
        const monthlyData = groupByMonth(allPayments);
        setPaymentData(monthlyData);
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load payment history');
    } finally {
      setLoading(false);
    }
  };

  const groupByMonth = (payments: any[]): PaymentData[] => {
    const now = new Date();
    const last6Months: PaymentData[] = [];

    // Generate last 6 months
    for (let i = 5; i >= 0; i--) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthKey = date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });

      const monthPayments = payments.filter((p) => {
        const paymentDate = new Date(p.payment_date);
        return (
          paymentDate.getMonth() === date.getMonth() &&
          paymentDate.getFullYear() === date.getFullYear()
        );
      });

      const totalAmount = monthPayments.reduce((sum, p) => sum + parseFloat(p.amount), 0);

      last6Months.push({
        month: monthKey,
        amount: totalAmount,
        count: monthPayments.length,
      });
    }

    return last6Months;
  };

  const maxAmount = Math.max(...paymentData.map((d) => d.amount), 1);

  if (loading) {
    return (
      <div className="payment-history-chart">
        <h3>Payment History (Last 6 Months)</h3>
        <div className="chart-loading">Loading...</div>
      </div>
    );
  }

  if (error) {
    return null; // Silently fail for chart
  }

  const hasPayments = paymentData.some((d) => d.amount > 0);

  return (
    <div className="payment-history-chart">
      <h3>Payment History (Last 6 Months)</h3>
      {!hasPayments ? (
        <div className="chart-empty">
          <p>No payment history available yet</p>
        </div>
      ) : (
        <div className="chart-container">
          <div className="chart-bars">
            {paymentData.map((data, index) => (
              <div key={index} className="chart-bar-wrapper">
                <div
                  className="chart-bar"
                  style={{ height: `${(data.amount / maxAmount) * 100}%` }}
                  title={`₦${data.amount.toLocaleString()}`}
                >
                  {data.amount > 0 && (
                    <div className="bar-tooltip">
                      <div className="tooltip-amount">{formatCurrency(data.amount)}</div>
                      <div className="tooltip-count">{data.count} payment{data.count !== 1 ? 's' : ''}</div>
                    </div>
                  )}
                </div>
                <div className="chart-label">{data.month.split(' ')[0]}</div>
              </div>
            ))}
          </div>
          <div className="chart-summary">
            <div className="summary-item">
              <span className="summary-label">Total Paid (6 months):</span>
              <span className="summary-value">
                {formatCurrency(paymentData.reduce((sum, d) => sum + d.amount, 0))}
              </span>
            </div>
            <div className="summary-item">
              <span className="summary-label">Total Payments:</span>
              <span className="summary-value">
                {paymentData.reduce((sum, d) => sum + d.count, 0)}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
